import React from "react";
import { createPortal } from "react-dom";
import { Center } from "../css";

export type ModalType = "center" | "side" | "full" | "sideTop";

interface ModalProps {
  onClose: (props?: any) => void;
  showCloseIcon?: boolean;
  children: React.ReactNode;
  SideComponent?: () => JSX.Element;
  title: string;
  forceTitle?: string;
  className?: string;
  type?: ModalType;
}

const modalClassName = {
  center: "items-center justify-center",
  side: "justify-end",
  full: "items-center justify-center mx-auto",
  sideTop: "justify-end  bg-transparent pointer-events-none !top-[166px] !right-0 !bg-transparent",
};
const modalContentClassName = {
  center: "w-full max-w-md",
  side: "w-full max-w-md",
  full: "w-[calc(100%-200px)]  h-[calc(100%-50px)]",
  sideTop: "w-full max-w-[330px] pointer-events-auto",
};

const Modal: React.FC<ModalProps> = ({ onClose, children, SideComponent, title, className = "", forceTitle, type = "center", showCloseIcon = true }) => {
  // Only handle body scroll
  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const modalRoot = document.getElementById("modal-root");

  if (!modalRoot) {
    console.error("Modal root element not found");
    return null;
  }

  const modalContent = (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex z-50 ${modalClassName[type]}`}>
      <div
        className={`bg-white rounded-llg shadow-lg  ${className} ${modalContentClassName[type]}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="flex justify-between items-center py-3.5 px-4 sticky top-0 bg-white z-10 rounded-t-llg border ">
          <h3 id="modal-title" className="font-bold text-text-default text-base !leading-norma">
            {forceTitle || title}
          </h3>
          <Center className="gap-5">
            {SideComponent && <SideComponent />}

            {showCloseIcon && (
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500" aria-label="Close">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </Center>
        </div>
        {children}
      </div>
    </div>
  );

  return createPortal(modalContent, modalRoot!);
};

export default Modal;

interface AbsoluteModalProps {
  onClose: (props: any) => void;
  showCloseIcon?: boolean;
  children: React.ReactNode;
  title: string;
  className?: string;
  type?: ModalType;
}

export const AbsoluteModal: React.FC<AbsoluteModalProps> = ({ onClose, showCloseIcon, title, className, type, children }) => {
  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex z-50 ${modalClassName[type as ModalType]}`}>
      <div className={`bg-white rounded-llg shadow-lg w-full max-w-md ${className}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="flex justify-between items-center py-3.5 px-4 sticky top-0 bg-white z-10 rounded-t-llg">
          <h3 id="modal-title" className="font-bold text-text-default text-base !leading-normal">
            {title}
          </h3>
          {showCloseIcon && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500" aria-label="Close">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};
